import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import PageHeader from "../-components/PageHeader";
import ConsoleHrozontalHeadTable from "../-components/ConsolehorizontalHeadTable";
import LoadingPop from "components/popup/LoadingPop";
import ConfirmDialog from "components/popup/ConfirmDialog";
import Seo from "components/Seo";
import { toastErrorSyle } from "hooks/utils/util";
import { useConsoleAuthStore } from "store/useConsoleAuthStore";
import { useGetConsoleAdminAccount } from "hooks/console/useGetConsoleAdminAccount";
import { usePostConsoleAdminAccount } from "hooks/console/usePostConsoleAdminAccount";
import { usePutConsoleAdminAccount } from "hooks/console/usePutConsoleAdminAccount";
import { useDeleteConsoleAdminAccount } from "hooks/console/useDeleteConsoleAdminAccount";

const theadStyle = "text-[#222222] text-[16px] text-center";

type Props = { mode: "create" | "edit" };

export default function ConsoleAdminAccountForm({ mode }: Props) {
    const navigate = useNavigate();
    const { idx } = useParams<{ idx: string }>();
    const isEdit = mode === "edit";

    const [form, setForm] = useState({ a_id: "", a_pwd: "", a_name: "" });

    const currentAId = useConsoleAuthStore(state => state.a_id);

    const { data: detail, isLoading: isDetailLoading } = useGetConsoleAdminAccount(isEdit ? idx : undefined);
    const { mutate: createAccount, isPending: isCreating } = usePostConsoleAdminAccount();
    const { mutate: updateAccount, isPending: isUpdating } = usePutConsoleAdminAccount();
    const { mutate: deleteAccount, isPending: isDeleting } = useDeleteConsoleAdminAccount();

    const isSelf = isEdit && !!detail?.a_id && detail.a_id === currentAId;

    useEffect(() => {
        if (isEdit && detail) {
            setForm({ a_id: detail.a_id ?? "", a_pwd: "", a_name: detail.a_name ?? "" });
        }
    }, [isEdit, detail]);

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();

        if (!isEdit) {
            if (!form.a_id.trim()) return toast.error("아이디를 입력해주세요.", toastErrorSyle);
            if (!form.a_pwd.trim()) return toast.error("비밀번호를 입력해주세요.", toastErrorSyle);
            if (!form.a_name.trim()) return toast.error("이름을 입력해주세요.", toastErrorSyle);

            createAccount(
                { a_id: form.a_id.trim(), a_pwd: form.a_pwd, a_name: form.a_name.trim() },
                {
                    onSuccess: () => {
                        toast.success("관리자 계정이 등록되었습니다.");
                        navigate("/admin/account");
                    },
                    onError: (error: any) => {
                        const message = error?.response?.data?.message ?? error.message;
                        toast.error(message, toastErrorSyle);
                    },
                },
            );
            return;
        }

        if (!form.a_name.trim()) return toast.error("이름을 입력해주세요.", toastErrorSyle);

        const body: { idx: string; a_name: string; a_pwd?: string } = {
            idx: idx!,
            a_name: form.a_name.trim(),
        };
        if (form.a_pwd.length > 0) body.a_pwd = form.a_pwd;

        updateAccount(body, {
            onSuccess: () => {
                toast.success("관리자 계정이 수정되었습니다.");
                navigate("/admin/account");
            },
            onError: (error: any) => {
                const message = error?.response?.data?.message ?? error.message;
                toast.error(message, toastErrorSyle);
            },
        });
    };

    const handleDelete = (close: () => void) => {
        if (!idx) return;
        deleteAccount(
            { idx },
            {
                onSuccess: () => {
                    close();
                    toast.success("관리자 계정이 삭제되었습니다.");
                    navigate("/admin/account");
                },
                onError: (error: any) => {
                    close();
                    const message = error?.response?.data?.message ?? error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    };

    const isPending = isCreating || isUpdating || isDeleting || (isEdit && isDetailLoading);

    return (
        <>
            <Seo type="console" pageTitle={isEdit ? "관리자 계정 수정" : "관리자 계정 등록"} />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="관리자" title={isEdit ? "계정 수정" : "계정 등록"} />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-y-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[700px] max-w-[900px]">
                            {isPending ? (
                                <LoadingPop />
                            ) : (
                                <form onSubmit={onSubmit}>
                                    <ConsoleHrozontalHeadTable
                                        thead={[
                                            {
                                                title: "아이디",
                                                key: "a_id",
                                                tdClassName: twMerge(theadStyle, "text-[#666666] text-left"),
                                                content: () => (
                                                    <input
                                                        type="text"
                                                        value={form.a_id}
                                                        readOnly={isEdit}
                                                        maxLength={12}
                                                        placeholder="아이디 (영문/숫자, 최대 12자)"
                                                        onChange={e => setForm(prev => ({ ...prev, a_id: e.target.value }))}
                                                        className={twMerge(
                                                            "border rounded-[5px] px-2 py-1 w-full",
                                                            isEdit && "bg-[#F7F7F8] cursor-not-allowed",
                                                        )}
                                                    />
                                                ),
                                            },
                                            {
                                                title: "비밀번호",
                                                key: "a_pwd",
                                                tdClassName: twMerge(theadStyle, "text-[#666666] text-left"),
                                                content: () => (
                                                    <div className="flex flex-col gap-1">
                                                        <input
                                                            type="password"
                                                            value={form.a_pwd}
                                                            maxLength={12}
                                                            placeholder={
                                                                isEdit
                                                                    ? "변경할 비밀번호 (비워두면 변경하지 않음)"
                                                                    : "비밀번호 (최대 12자)"
                                                            }
                                                            onChange={e => setForm(prev => ({ ...prev, a_pwd: e.target.value }))}
                                                            className="border rounded-[5px] px-2 py-1 w-full"
                                                        />
                                                    </div>
                                                ),
                                            },
                                            {
                                                title: "이름",
                                                key: "a_name",
                                                tdClassName: twMerge(theadStyle, "text-[#666666] text-left"),
                                                content: () => (
                                                    <input
                                                        type="text"
                                                        value={form.a_name}
                                                        maxLength={20}
                                                        placeholder="이름 (최대 20자)"
                                                        onChange={e => setForm(prev => ({ ...prev, a_name: e.target.value }))}
                                                        className="border rounded-[5px] px-2 py-1 w-full"
                                                    />
                                                ),
                                            },
                                        ]}
                                        tbody={form}
                                    />

                                    <div className="flex justify-center gap-2 mt-7">
                                        <button type="submit" className="bg-black text-white rounded-[5px] px-6 py-2">
                                            {isEdit ? "수정" : "저장"}
                                        </button>
                                        <button
                                            type="button"
                                            className="bg-[#dddddd] text-black rounded-[5px] px-6 py-2"
                                            onClick={() => navigate("/admin/account")}
                                        >
                                            목록
                                        </button>
                                        {isEdit && (
                                            <ConfirmDialog
                                                title="관리자 계정 삭제"
                                                trigger={open => (
                                                    <button
                                                        type="button"
                                                        disabled={isSelf}
                                                        title={isSelf ? "본인 계정은 삭제할 수 없습니다." : ""}
                                                        onClick={() => {
                                                            if (isSelf) {
                                                                toast.error("본인 계정은 삭제할 수 없습니다.", toastErrorSyle);
                                                                return;
                                                            }
                                                            open();
                                                        }}
                                                        className={twMerge(
                                                            "bg-[#FF5463] text-white rounded-[5px] px-6 py-2",
                                                            isSelf && "bg-[#F0A5AC] cursor-not-allowed",
                                                        )}
                                                    >
                                                        삭제
                                                    </button>
                                                )}
                                                handleConfirm={handleDelete}
                                            />
                                        )}
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
